import { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import { FaUniversalAccess } from "react-icons/fa";

export default function AccessibilityWidget({ direction = "LTR" }) {
    const [isOpen, setIsOpen] = useState(false);
    const [fontScale, setFontScale] = useState(
        Number(localStorage.getItem("a11y-fontScale")) || 1
    );
    const [grayscale, setGrayscale] = useState(
        localStorage.getItem("a11y-grayscale") === "true"
    );
    const [highContrast, setHighContrast] = useState(
        localStorage.getItem("a11y-contrast") === "true"
    );

    const panelRef = useRef(null);
    const buttonRef = useRef(null);

    // ===== Font scaling
    useEffect(() => {
        document.documentElement.style.fontSize = `${fontScale * 100}%`;
        localStorage.setItem("a11y-fontScale", fontScale);
    }, [fontScale]);

    // ===== Grayscale
    useEffect(() => {
        document.documentElement.classList.toggle("grayscale", grayscale);
        localStorage.setItem("a11y-grayscale", grayscale);
    }, [grayscale]);

    // ===== High contrast
    useEffect(() => {
        document.documentElement.classList.toggle("high-contrast", highContrast);
        localStorage.setItem("a11y-contrast", highContrast);
    }, [highContrast]);

    // ===== Close on outside click
    useEffect(() => {
        function handleClickOutside(e) {
            if (
                panelRef.current &&
                !panelRef.current.contains(e.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(e.target)
            ) {
                setIsOpen(false);
            }
        }

        if (isOpen) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    const resetAll = () => {
        setFontScale(1);
        setGrayscale(false);
        setHighContrast(false);
    };

    return (
        <>
            {/* Floating Button */}
            <button
                ref={buttonRef}
                onClick={() => setIsOpen(!isOpen)}
                className={`fixed bottom-6 ${direction === "RTL" ? "left-6" : "right-6"
                    } z-50 bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-xl transition-all`} aria-label="Accessibility options"
            >
                <FaUniversalAccess size={22} />
            </button>

            {/* Panel */}
            {isOpen && (
                <div
                    ref={panelRef}
                    className={`fixed bottom-24 ${direction === "RTL" ? "left-6" : "right-6"
                        } z-50 w-72 bg-white rounded-2xl shadow-2xl border border-slate-200 p-5 space-y-4`}
                >
                    <div className="flex justify-between items-center">
                        <div className="font-semibold text-slate-800">
                            Accessibility
                        </div>
                        <button onClick={() => setIsOpen(false)}>
                            <X size={18} />
                        </button>
                    </div>

                    {/* Font Size */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => setFontScale((s) => Math.min(s + 0.1, 1.5))}
                            className="flex-1 bg-slate-100 hover:bg-slate-200 rounded-lg py-2 font-semibold"
                        >
                            A+
                        </button>
                        <button
                            onClick={() => setFontScale((s) => Math.max(s - 0.1, 0.8))}
                            className="flex-1 bg-slate-100 hover:bg-slate-200 rounded-lg py-2 font-semibold"
                        >
                            A-
                        </button>
                    </div>

                    {/* Grayscale */}
                    <button
                        onClick={() => setGrayscale(!grayscale)}
                        className={`w-full rounded-lg py-2 transition ${grayscale
                                ? "bg-blue-600 text-white"
                                : "bg-slate-100 hover:bg-slate-200"
                            }`}
                    >
                        Grayscale
                    </button>

                    {/* High Contrast */}
                    <button
                        onClick={() => setHighContrast(!highContrast)}
                        className={`w-full rounded-lg py-2 transition ${highContrast
                                ? "bg-blue-600 text-white"
                                : "bg-slate-100 hover:bg-slate-200"
                            }`}
                    >
                        High Contrast
                    </button>

                    {/* Reset */}
                    <button
                        onClick={resetAll}
                        className="w-full bg-red-100 hover:bg-red-200 text-red-600 rounded-lg py-2 font-semibold"
                    >
                        Reset
                    </button>
                </div>
            )}
        </>
    );
}